// components/SettingsDialog.js
import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  InputAdornment,
} from '@mui/material'

import {
  CustomInputLabel,
  CustomTextField,
} from '../../components/restyled-mui/CustomInputs'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { getSettingsStyles } from '../../styles/settingsStyles'
import { makeStyles } from '@mui/styles'
import { validatePassword } from './utils'
import Api from '../../api'
import { useDispatch } from 'react-redux'
import { changeStatus } from '../../redux/actions/status'
import { STATUS } from '../../redux/types'

const useStyles = makeStyles(theme => ({
  ...getSettingsStyles(theme),
  passwordContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
    justifyContent: 'space-between',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
}))

const validateOldPassword = (value, userPassword) => {
  if (!value.trim()) return 'Old password is required'
  if (value !== userPassword) return 'Incorrect current password'
  return ''
}

const validateNewPassword = value => {
  if (!value.trim()) return 'New password is required'
  const result = validatePassword(value)
  return result.valid ? '' : result.errors[0] || 'Weak password'
}

const validateConfirmNewPassword = (value, newPassword) => {
  if (!value.trim()) return 'Confirmation is required'
  if (value !== newPassword) return 'Passwords do not match'
  return ''
}

const PasswordSettingsDialog = props => {
  const { user, open, onClose } = props

  const dispatch = useDispatch()

  const [formState, setFormState] = useState({
    oldPassword: '',
    oldPasswordError: '',
    newPassword: '',
    newPasswordError: '',
    confirmNewPassword: '',
    confirmNewPasswordError: '',
  })

  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  })

  const handleTextInputChange = name => e => {
    const value = e.target.value

    setFormState(prev => {
      let error = ''

      if (name === 'oldPassword') {
        error = validateOldPassword(value, user.password)
      }

      if (name === 'newPassword') {
        error = validateNewPassword(value)

        // Also check confirm password again if it's already filled
        if (prev.confirmNewPassword) {
          const confirmError = validateConfirmNewPassword(
            prev.confirmNewPassword,
            value,
          )
          return {
            ...prev,
            [name]: value,
            [`${name}Error`]: error,
            confirmNewPasswordError: confirmError,
          }
        }
      }

      if (name === 'confirmNewPassword') {
        error = validateConfirmNewPassword(value, prev.newPassword)
      }

      return {
        ...prev,
        [name]: value,
        [`${name}Error`]: error,
      }
    })
  }

  const handleSave = async () => {
    const { oldPassword, newPassword, confirmNewPassword } = formState

    const errors = {
      oldPasswordError: validateOldPassword(oldPassword, user.password),
      newPasswordError: validateNewPassword(newPassword),
      confirmNewPasswordError: validateConfirmNewPassword(
        confirmNewPassword,
        newPassword,
      ),
    }

    const hasError = Object.values(errors).some(Boolean)

    setFormState(prev => ({
      ...prev,
      ...errors,
    }))

    if (hasError) return

    try {
      const response = await Api.Users.changeUserPassword({
        new_password: formState.newPassword,
        old_password: formState.oldPassword,
      })

      if (response.status === 200) {
        dispatch(changeStatus(STATUS.SUCCESS, response.data.msg))
        onClose()
      } else {
        dispatch(
          changeStatus(
            STATUS.ERROR,
            response.data?.msg ||
              'Something went wrong.Unexpected error occurred.',
          ),
        )
      }
    } catch (error) {
      console.error('Failed to save user password', error)
    }
  }

  const handleTogglePasswordVisibility = field => () => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const hasErrors = Object.entries(formState).some(
    ([key, value]) =>
      (key.endsWith('Error') && value) ||
      (!key.endsWith('Error') && value.trim() === ''),
  )

  const passwordInput = (label, name) => (
    <>
      <CustomInputLabel>{label}</CustomInputLabel>
      <CustomTextField
        variant="outlined"
        margin="dense"
        size="small"
        fullWidth
        type={showPassword[name] ? 'text' : 'password'}
        autoComplete="new-password"
        name={name}
        value={formState[name]}
        onChange={handleTextInputChange(name)}
        error={Boolean(formState[`${name}Error`])}
        helperText={formState[`${name}Error`]}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleTogglePasswordVisibility(name)}
                edge="end"
                aria-label="toggle password visibility"
              >
                {showPassword[name] ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        {passwordInput('Old password', 'oldPassword')}
        {passwordInput('New password', 'newPassword')}
        {passwordInput('Confirm new password', 'confirmNewPassword')}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={hasErrors}
          variant="contained"
          color="primary"
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PasswordSettingsDialog
